// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useGeneratedCourse.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useMutation, UseMutationResult} from '@tanstack/react-query';

import {CoachStep} from '../../Screens/LMS/coachNarration';

import {CoachAnswer} from './types';

/** One exchange already had in this conversation. */
export type CoachTurn = {
  question: string;
  answer: string;
  chapters: number[];
  /**
   * A narrated demonstration, when the coach answered with one. Each step is
   * one sentence plus the position to show while it is said; every move was
   * replayed on a real board server-side before it got here.
   */
  steps?: CoachStep[];
  /**
   * A hint the student asked the board for, not a question they typed. It is
   * shown differently for that reason — labelling it "you asked" would be a
   * small lie, and the coach did not compose it either: it is pre-generated
   * and stored on the page.
   */
  kind?: 'answer' | 'hint';
};

export type AskCoachPayload = {
  bookUuid: string;
  question: string;
  /**
   * The conversation so far, oldest first. Sent with every question because
   * the server keeps nothing: this is what makes "why that one?" answerable.
   */
  history?: CoachTurn[];
  /**
   * The chapter the student is looking at. Sent so they never have to say
   * "the puzzle I have open" — the app already knows which one that is.
   */
  currentChapter?: number | null;
};

export async function askCoach({
  bookUuid,
  question,
  history,
  currentChapter,
}: AskCoachPayload): Promise<CoachAnswer> {
  const token = await getLocalStorage('access_token');
  const response = await ChessRestClient.post(
    `/video_learning/course/${bookUuid}/ask/`,
    {
      question,
      current_chapter: currentChapter ?? null,
      history: (history ?? []).map(t => ({
        question: t.question,
        answer: t.answer,
      })),
    },
    {headers: {Authorization: `Bearer ${token}`}},
  );
  return response.data as CoachAnswer;
}

/**
 * One question about the course as a whole.
 *
 * A mutation rather than a query, for the same reason as `useAskTutor`: asking
 * costs something, and the answer depends on how far the student has got, so
 * the same question later is a different question.
 */
export function useAskCoach(): UseMutationResult<
  CoachAnswer,
  Error,
  AskCoachPayload
> {
  return useMutation<CoachAnswer, Error, AskCoachPayload>(askCoach);
}
